/**
 * ESG Impact Assessment Service
 * Comprehensive environmental, social, and governance impact measurement and monitoring
 * Following industry standards for ESG impact assessment and sustainability reporting
 * 
 * Standards Compliance:
 * - GRI Standards: Comprehensive ESG Impact Reporting
 * - SASB Materiality Map: Industry-Specific Impact Measurement
 * - TCFD: Climate-Related Financial Disclosures
 * - EU Taxonomy: Environmental Impact Classification
 * - UN SDGs: Sustainable Development Goals Alignment
 * - CDP: Environmental Impact Disclosure
 * - CDSB: Climate and Environmental Reporting
 * - IIRC: Integrated Reporting Framework
 * - PCAF: Portfolio Carbon Accounting
 * - SBTi: Science-Based Targets Initiative
 */

const ESG_IMPACT_ASSESSMENT_KEY = 'esg-impact-assessment-v1';

// Industry-standard seed data for ESG impact assessment
const esgImpactAssessmentSeed = {
  // Environmental Impact Assessments
  environmentalImpacts: [
    {
      id: 'env-001',
      category: 'Climate Change',
      subcategory: 'GHG Emissions',
      scope: 'Global Operations',
      materialityScore: 9.2,
      impactType: 'Negative',
      severity: 'High',
      likelihood: 85,
      timeHorizon: 'Medium-term (5-10 years)',
      metrics: {
        scope1Emissions: { value: 24567, unit: 'tCO2e', trend: '-12.3%', target: 18000 },
        scope2Emissions: { value: 45892, unit: 'tCO2e', trend: '-8.7%', target: 32000 },
        scope3Emissions: { value: 178456, unit: 'tCO2e', trend: '-4.2%', target: 125000 },
        carbonIntensity: { value: 2.4, unit: 'tCO2e/M revenue', trend: '-15.6%', target: 1.8 },
        renewableEnergy: { value: 68.4, unit: '%', trend: '+12.8%', target: 80.0 }
      },
      mitigationMeasures: [
        { measure: 'Renewable Energy Transition', investment: 15600000, effectiveness: 89.2, timeline: '2024-2027' },
        { measure: 'Energy Efficiency Programs', investment: 8900000, effectiveness: 76.8, timeline: '2024-2026' },
        { measure: 'Carbon Offset Portfolio', investment: 3400000, effectiveness: 62.4, timeline: '2024-2025' },
        { measure: 'Supplier Emission Reduction', investment: 5200000, effectiveness: 83.7, timeline: '2024-2028' }
      ],
      stakeholderImpact: {
        communities: { severity: 'High', nature: 'Air quality, health impacts', affected: 125000 },
        employees: { severity: 'Medium', nature: 'Health, workplace safety', affected: 8500 },
        investors: { severity: 'High', nature: 'Financial risk, reputation', affected: 'All shareholders' },
        regulators: { severity: 'High', nature: 'Compliance, reporting', affected: 'Multiple jurisdictions' }
      },
      complianceFrameworks: ['TCFD', 'CDP', 'SBTi', 'EU Taxonomy'],
      lastAssessment: '2024-09-15T10:30:00Z',
      nextReview: '2024-12-15T14:00:00Z'
    },
    {
      id: 'env-002',
      category: 'Resource Management',
      subcategory: 'Water Stewardship',
      scope: 'High-Risk Watersheds',
      materialityScore: 7.8,
      impactType: 'Negative',
      severity: 'Medium',
      likelihood: 72,
      timeHorizon: 'Short-term (1-3 years)',
      metrics: {
        waterConsumption: { value: 2.8, unit: 'million m³', trend: '-5.4%', target: 2.4 },
        waterIntensity: { value: 3.2, unit: 'm³/unit production', trend: '-7.8%', target: 2.8 },
        waterRecycling: { value: 42.6, unit: '%', trend: '+8.9%', target: 55.0 },
        waterQuality: { value: 94.3, unit: '% compliance', trend: '+2.1%', target: 98.0 },
        waterRisk: { value: 'Medium-High', unit: 'WWF Risk Assessment', trend: 'Stable', target: 'Medium' }
      },
      mitigationMeasures: [
        { measure: 'Water Efficiency Technology', investment: 4200000, effectiveness: 81.5, timeline: '2024-2025' },
        { measure: 'Watershed Restoration', investment: 2800000, effectiveness: 74.2, timeline: '2024-2027' },
        { measure: 'Advanced Water Treatment', investment: 6700000, effectiveness: 92.1, timeline: '2024-2026' },
        { measure: 'Community Water Projects', investment: 1900000, effectiveness: 68.9, timeline: '2024-2025' }
      ],
      stakeholderImpact: {
        communities: { severity: 'High', nature: 'Water access, quality', affected: 89000 },
        employees: { severity: 'Low', nature: 'Facility operations', affected: 3200 },
        suppliers: { severity: 'Medium', nature: 'Supply chain risk', affected: 156 },
        ngos: { severity: 'Medium', nature: 'Environmental advocacy', affected: 'Multiple organizations' }
      },
      complianceFrameworks: ['CDP Water', 'CEO Water Mandate', 'AWS Standard'],
      lastAssessment: '2024-09-08T14:15:00Z',
      nextReview: '2024-11-30T09:00:00Z'
    },
    {
      id: 'env-003',
      category: 'Biodiversity',
      subcategory: 'Ecosystem Protection',
      scope: 'Operations in Protected Areas',
      materialityScore: 6.4,
      impactType: 'Mixed',
      severity: 'Medium',
      likelihood: 64,
      timeHorizon: 'Long-term (10+ years)',
      metrics: {
        biodiversityFootprint: { value: 1847, unit: 'hectares affected', trend: '-3.2%', target: 1600 },
        habitatProtected: { value: 3420, unit: 'hectares conserved', trend: '+12.4%', target: 4000 },
        speciesMonitored: { value: 127, unit: 'species tracked', trend: '+8.7%', target: 150 },
        nativeSpeciesIndex: { value: 78.4, unit: '% diversity maintained', trend: '+2.3%', target: 85.0 },
        restorationProgress: { value: 68.9, unit: '% restoration targets met', trend: '+15.6%', target: 80.0 }
      },
      mitigationMeasures: [
        { measure: 'Habitat Restoration Program', investment: 5600000, effectiveness: 86.3, timeline: '2024-2029' },
        { measure: 'Biodiversity Monitoring System', investment: 2100000, effectiveness: 92.7, timeline: '2024-2026' },
        { measure: 'Conservation Partnerships', investment: 3800000, effectiveness: 79.4, timeline: '2024-2027' },
        { measure: 'No Net Loss Strategy', investment: 8900000, effectiveness: 84.1, timeline: '2024-2030' }
      ],
      stakeholderImpact: {
        communities: { severity: 'Medium', nature: 'Ecosystem services', affected: 45000 },
        scientists: { severity: 'Low', nature: 'Research collaboration', affected: 28 },
        ngos: { severity: 'High', nature: 'Conservation advocacy', affected: 'Multiple organizations' },
        governments: { severity: 'Medium', nature: 'Policy compliance', affected: '3 jurisdictions' }
      },
      complianceFrameworks: ['CBD', 'CITES', 'IUCN Red List', 'TNFD'],
      lastAssessment: '2024-08-22T11:20:00Z',
      nextReview: '2024-12-01T16:30:00Z'
    }
  ],

  // Social Impact Assessments
  socialImpacts: [
    {
      id: 'soc-001',
      category: 'Human Rights',
      subcategory: 'Labor Practices',
      scope: 'Global Supply Chain',
      materialityScore: 8.7,
      impactType: 'Negative',
      severity: 'High',
      likelihood: 78,
      timeHorizon: 'Ongoing',
      metrics: {
        supplierAudits: { value: 89.3, unit: '% suppliers audited', trend: '+5.7%', target: 95.0 },
        laborViolations: { value: 12, unit: 'incidents', trend: '-34.6%', target: 5 },
        workerTraining: { value: 94.7, unit: '% workers trained', trend: '+8.2%', target: 98.0 },
        grievancesResolved: { value: 91.4, unit: '% within 30 days', trend: '+6.8%', target: 95.0 },
        livingWageCompliance: { value: 76.8, unit: '% suppliers compliant', trend: '+12.3%', target: 85.0 }
      },
      mitigationMeasures: [
        { measure: 'Supplier Capacity Building', investment: 7800000, effectiveness: 84.6, timeline: '2024-2026' },
        { measure: 'Worker Empowerment Programs', investment: 4500000, effectiveness: 91.2, timeline: '2024-2025' },
        { measure: 'Digital Monitoring Systems', investment: 3200000, effectiveness: 87.9, timeline: '2024-2025' },
        { measure: 'Living Wage Initiative', investment: 12600000, effectiveness: 78.3, timeline: '2024-2027' }
      ],
      stakeholderImpact: {
        workers: { severity: 'High', nature: 'Working conditions, rights', affected: 145000 },
        communities: { severity: 'Medium', nature: 'Economic opportunities', affected: 67000 },
        ngos: { severity: 'High', nature: 'Human rights advocacy', affected: 'Multiple organizations' },
        investors: { severity: 'Medium', nature: 'Reputational risk', affected: 'All shareholders' }
      },
      complianceFrameworks: ['UN Global Compact', 'ILO Conventions', 'UN Guiding Principles', 'OECD Guidelines'],
      lastAssessment: '2024-09-10T13:45:00Z',
      nextReview: '2024-11-15T10:00:00Z'
    },
    {
      id: 'soc-002',
      category: 'Community Development',
      subcategory: 'Local Economic Impact',
      scope: 'Operating Communities',
      materialityScore: 7.2,
      impactType: 'Positive',
      severity: 'Medium',
      likelihood: 92,
      timeHorizon: 'Medium-term (5-10 years)',
      metrics: {
        localProcurement: { value: 67.4, unit: '% local suppliers', trend: '+9.1%', target: 75.0 },
        localEmployment: { value: 82.6, unit: '% local hires', trend: '+4.3%', target: 85.0 },
        communityInvestment: { value: 8.9, unit: 'M USD invested', trend: '+15.7%', target: 12.0 },
        skillsDevelopment: { value: 4567, unit: 'people trained', trend: '+22.4%', target: 6000 },
        smallBusinessSupport: { value: 234, unit: 'businesses supported', trend: '+18.9%', target: 300 }
      },
      mitigationMeasures: [
        { measure: 'Local Supplier Development', investment: 5400000, effectiveness: 89.7, timeline: '2024-2026' },
        { measure: 'Community Skills Centers', investment: 6800000, effectiveness: 92.4, timeline: '2024-2027' },
        { measure: 'Youth Employment Program', investment: 3600000, effectiveness: 85.1, timeline: '2024-2025' },
        { measure: 'Women Entrepreneurship', investment: 2900000, effectiveness: 88.6, timeline: '2024-2026' }
      ],
      stakeholderImpact: {
        communities: { severity: 'High', nature: 'Economic development', affected: 78000 },
        smallbusinesses: { severity: 'High', nature: 'Market access, growth', affected: 890 },
        youth: { severity: 'Medium', nature: 'Skills, employment', affected: 12000 },
        women: { severity: 'Medium', nature: 'Economic empowerment', affected: 5600 }
      },
      complianceFrameworks: ['IFC Performance Standards', 'UN SDGs', 'GRI 413'],
      lastAssessment: '2024-09-03T09:30:00Z',
      nextReview: '2024-12-03T14:15:00Z'
    },
    {
      id: 'soc-003',
      category: 'Product Responsibility',
      subcategory: 'Health and Safety',
      scope: 'Product Lifecycle',
      materialityScore: 8.4,
      impactType: 'Mixed',
      severity: 'Medium',
      likelihood: 68,
      timeHorizon: 'Short-term (1-3 years)',
      metrics: {
        productSafety: { value: 99.7, unit: '% compliance rate', trend: '+0.3%', target: 99.9 },
        safetyIncidents: { value: 3, unit: 'reported incidents', trend: '-40.0%', target: 1 },
        healthBenefits: { value: 2.4, unit: 'M people benefited', trend: '+18.7%', target: 3.0 },
        accessibilityScore: { value: 76.8, unit: '% accessibility compliance', trend: '+12.4%', target: 85.0 },
        customerSatisfaction: { value: 87.9, unit: '% satisfaction', trend: '+3.2%', target: 90.0 }
      },
      mitigationMeasures: [
        { measure: 'Enhanced Product Testing', investment: 4700000, effectiveness: 94.2, timeline: '2024-2025' },
        { measure: 'Accessibility Design Program', investment: 3100000, effectiveness: 82.6, timeline: '2024-2026' },
        { measure: 'Consumer Education Initiative', investment: 2800000, effectiveness: 78.9, timeline: '2024-2025' },
        { measure: 'Health Impact Studies', investment: 1900000, effectiveness: 91.3, timeline: '2024-2027' }
      ],
      stakeholderImpact: {
        consumers: { severity: 'High', nature: 'Health, safety, access', affected: 2400000 },
        patients: { severity: 'High', nature: 'Treatment outcomes', affected: 156000 },
        healthcare: { severity: 'Medium', nature: 'System efficiency', affected: '1200 facilities' },
        regulators: { severity: 'Medium', nature: 'Safety oversight', affected: '15 agencies' }
      },
      complianceFrameworks: ['ISO 14155', 'ICH-GCP', 'FDA Guidelines', 'EMA Standards'],
      lastAssessment: '2024-09-12T16:00:00Z',
      nextReview: '2024-10-30T11:30:00Z'
    }
  ],

  // Governance Impact Assessments
  governanceImpacts: [
    {
      id: 'gov-001',
      category: 'Corporate Governance',
      subcategory: 'Board Effectiveness',
      scope: 'Corporate Leadership',
      materialityScore: 7.9,
      impactType: 'Positive',
      severity: 'Medium',
      likelihood: 89,
      timeHorizon: 'Medium-term (5-10 years)',
      metrics: {
        boardIndependence: { value: 78.6, unit: '% independent directors', trend: '+7.1%', target: 80.0 },
        genderDiversity: { value: 42.9, unit: '% women directors', trend: '+14.3%', target: 50.0 },
        skillsDiversity: { value: 91.2, unit: '% skills coverage', trend: '+5.8%', target: 95.0 },
        meetingAttendance: { value: 96.4, unit: '% attendance rate', trend: '+1.8%', target: 98.0 },
        esgOversight: { value: 87.5, unit: '% ESG integration', trend: '+12.7%', target: 95.0 }
      },
      mitigationMeasures: [
        { measure: 'Director Development Program', investment: 1200000, effectiveness: 88.4, timeline: '2024-2025' },
        { measure: 'ESG Training Initiative', investment: 800000, effectiveness: 92.1, timeline: '2024-2025' },
        { measure: 'Board Diversity Strategy', investment: 600000, effectiveness: 79.6, timeline: '2024-2026' },
        { measure: 'Governance Technology', investment: 1500000, effectiveness: 85.7, timeline: '2024-2025' }
      ],
      stakeholderImpact: {
        shareholders: { severity: 'High', nature: 'Governance quality', affected: 'All shareholders' },
        employees: { severity: 'Medium', nature: 'Leadership direction', affected: 8500 },
        investors: { severity: 'High', nature: 'Decision oversight', affected: 'Institutional investors' },
        regulators: { severity: 'Low', nature: 'Compliance monitoring', affected: 'Multiple agencies' }
      },
      complianceFrameworks: ['OECD Principles', 'King IV', 'UK Corporate Governance Code', 'NYSE Guidelines'],
      lastAssessment: '2024-08-30T14:20:00Z',
      nextReview: '2024-11-30T10:45:00Z'
    },
    {
      id: 'gov-002',
      category: 'Business Ethics',
      subcategory: 'Anti-Corruption',
      scope: 'Global Operations',
      materialityScore: 9.1,
      impactType: 'Negative',
      severity: 'High',
      likelihood: 34,
      timeHorizon: 'Ongoing',
      metrics: {
        ethicsTraining: { value: 98.7, unit: '% employees trained', trend: '+2.1%', target: 100.0 },
        whistleblowerReports: { value: 47, unit: 'reports received', trend: '+12.4%', target: 'Monitor' },
        investigationTime: { value: 18.6, unit: 'average days', trend: '-15.9%', target: 15.0 },
        complianceViolations: { value: 2, unit: 'confirmed cases', trend: '-66.7%', target: 0 },
        dueDiligenceScore: { value: 94.3, unit: '% partners screened', trend: '+3.8%', target: 98.0 }
      },
      mitigationMeasures: [
        { measure: 'Enhanced Due Diligence', investment: 2400000, effectiveness: 91.8, timeline: '2024-2025' },
        { measure: 'Ethics Culture Program', investment: 1800000, effectiveness: 86.7, timeline: '2024-2026' },
        { measure: 'AI Monitoring Systems', investment: 3200000, effectiveness: 94.5, timeline: '2024-2025' },
        { measure: 'Third-Party Risk Management', investment: 2900000, effectiveness: 89.2, timeline: '2024-2025' }
      ],
      stakeholderImpact: {
        shareholders: { severity: 'High', nature: 'Reputational risk', affected: 'All shareholders' },
        employees: { severity: 'Medium', nature: 'Workplace culture', affected: 8500 },
        regulators: { severity: 'High', nature: 'Legal compliance', affected: 'Multiple jurisdictions' },
        partners: { severity: 'Medium', nature: 'Business relationships', affected: 1240 }
      },
      complianceFrameworks: ['FCPA', 'UK Bribery Act', 'UNGC Principle 10', 'ISO 37001'],
      lastAssessment: '2024-09-05T11:15:00Z',
      nextReview: '2024-12-05T15:30:00Z'
    }
  ],

  // Integrated Impact Metrics
  integratedMetrics: {
    overallESGScore: 84.7,
    materialityWeighting: {
      environmental: 35,
      social: 40,
      governance: 25
    },
    impactCategories: {
      environmental: { score: 82.4, trend: '+3.8%', riskLevel: 'Medium' },
      social: { score: 87.9, trend: '+5.2%', riskLevel: 'Low' },
      governance: { score: 83.1, trend: '+2.1%', riskLevel: 'Low' }
    },
    stakeholderValue: {
      shareholders: { value: 156.8, unit: 'M USD created', satisfaction: 84.7 },
      employees: { value: 89.3, unit: 'M USD wages/benefits', satisfaction: 87.2 },
      communities: { value: 23.4, unit: 'M USD invested', satisfaction: 89.6 },
      environment: { value: -12.7, unit: 'M USD externalities', improvement: 78.9 },
      customers: { value: 2847.6, unit: 'M USD value delivered', satisfaction: 88.1 }
    },
    sdgAlignment: {
      sdg3: { contribution: 'High', score: 89.4, initiatives: 8 },
      sdg8: { contribution: 'High', score: 91.2, initiatives: 12 },
      sdg13: { contribution: 'Very High', score: 86.7, initiatives: 15 },
      sdg16: { contribution: 'Medium', score: 87.9, initiatives: 6 },
      sdg17: { contribution: 'Medium', score: 84.3, initiatives: 9 }
    }
  },

  // Risk Assessment Matrix
  riskMatrix: [
    {
      category: 'Climate Physical Risk',
      subcategories: ['Extreme Weather', 'Sea Level Rise', 'Temperature Changes', 'Precipitation Patterns'],
      likelihood: 'High',
      impact: 'Very High',
      timeHorizon: 'Long-term',
      riskScore: 8.7,
      financialImpact: { min: 50000000, max: 200000000, currency: 'USD' },
      mitigationStatus: 'In Progress',
      monitoringFrequency: 'Quarterly'
    },
    {
      category: 'Transition Risk',
      subcategories: ['Policy Changes', 'Technology Shifts', 'Market Preferences', 'Reputation'],
      likelihood: 'Very High',
      impact: 'High',
      timeHorizon: 'Medium-term',
      riskScore: 8.2,
      financialImpact: { min: 30000000, max: 120000000, currency: 'USD' },
      mitigationStatus: 'Active',
      monitoringFrequency: 'Monthly'
    },
    {
      category: 'Social License Risk',
      subcategories: ['Community Relations', 'Human Rights', 'Labor Practices', 'Product Safety'],
      likelihood: 'Medium',
      impact: 'High',
      timeHorizon: 'Short-term',
      riskScore: 6.8,
      financialImpact: { min: 15000000, max: 80000000, currency: 'USD' },
      mitigationStatus: 'Comprehensive',
      monitoringFrequency: 'Monthly'
    },
    {
      category: 'Governance Risk',
      subcategories: ['Board Oversight', 'Ethics Violations', 'Regulatory Compliance', 'Transparency'],
      likelihood: 'Low',
      impact: 'Very High',
      timeHorizon: 'Short-term',
      riskScore: 5.4,
      financialImpact: { min: 10000000, max: 150000000, currency: 'USD' },
      mitigationStatus: 'Robust',
      monitoringFrequency: 'Continuous'
    }
  ],

  // Performance Monitoring
  performanceMonitoring: [
    {
      name: 'ESG Score Improvement',
      current: 84.7,
      target: 88.0,
      unit: 'Score',
      trend: '+3.2%',
      frequency: 'Quarterly',
      status: 'On Track'
    },
    {
      name: 'Carbon Footprint Reduction',
      current: 12.3,
      target: 25.0,
      unit: '% reduction vs baseline',
      trend: '+2.8%',
      frequency: 'Monthly',
      status: 'Needs Acceleration'
    },
    {
      name: 'Stakeholder Satisfaction',
      current: 87.1,
      target: 90.0,
      unit: '% satisfaction',
      trend: '+1.9%',
      frequency: 'Quarterly',
      status: 'On Track'
    },
    {
      name: 'Impact Investment ROI',
      current: 3.2,
      target: 2.5,
      unit: 'Ratio',
      trend: '+0.4',
      frequency: 'Annual',
      status: 'Exceeding'
    },
    {
      name: 'Risk Mitigation Progress',
      current: 78.9,
      target: 85.0,
      unit: '% risks mitigated',
      trend: '+6.7%',
      frequency: 'Monthly',
      status: 'On Track'
    },
    {
      name: 'Compliance Rate',
      current: 97.8,
      target: 99.0,
      unit: '% compliance',
      trend: '+1.2%',
      frequency: 'Continuous',
      status: 'On Track'
    }
  ]
};

// Service implementation
let impactAssessmentData = null;
let subscribers = [];

const initializeData = () => {
  if (!impactAssessmentData) {
    const stored = localStorage.getItem(ESG_IMPACT_ASSESSMENT_KEY);
    impactAssessmentData = stored ? JSON.parse(stored) : esgImpactAssessmentSeed;
    persistData();
  }
  return impactAssessmentData;
};

const persistData = () => {
  if (impactAssessmentData) {
    localStorage.setItem(ESG_IMPACT_ASSESSMENT_KEY, JSON.stringify(impactAssessmentData));
    notifySubscribers();
  }
};

const notifySubscribers = () => {
  subscribers.forEach(callback => callback(impactAssessmentData));
};

// Public API
export const subscribe = (callback) => {
  subscribers.push(callback);
  callback(initializeData());
  
  return () => {
    subscribers = subscribers.filter(sub => sub !== callback);
  };
};

export const getESGImpactAssessmentOverview = () => {
  const data = initializeData();
  return {
    totalImpacts: data.environmentalImpacts.length + data.socialImpacts.length + data.governanceImpacts.length,
    environmentalImpacts: data.environmentalImpacts.length,
    socialImpacts: data.socialImpacts.length,
    governanceImpacts: data.governanceImpacts.length,
    overallESGScore: data.integratedMetrics.overallESGScore,
    averageMaterialityScore: [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts]
      .reduce((sum, impact) => sum + impact.materialityScore, 0) / 
      (data.environmentalImpacts.length + data.socialImpacts.length + data.governanceImpacts.length),
    highRiskImpacts: [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts]
      .filter(impact => impact.severity === 'High' || impact.materialityScore >= 8.0).length,
    totalInvestment: [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts]
      .reduce((sum, impact) => sum + impact.mitigationMeasures.reduce((mSum, measure) => mSum + measure.investment, 0), 0),
    stakeholderValue: data.integratedMetrics.stakeholderValue,
    performanceStatus: data.performanceMonitoring.filter(kpi => kpi.status === 'On Track' || kpi.status === 'Exceeding').length
  };
};

export const getEnvironmentalImpacts = () => initializeData().environmentalImpacts;
export const getSocialImpacts = () => initializeData().socialImpacts;
export const getGovernanceImpacts = () => initializeData().governanceImpacts;
export const getIntegratedMetrics = () => initializeData().integratedMetrics;
export const getRiskMatrix = () => initializeData().riskMatrix;
export const getPerformanceMonitoring = () => initializeData().performanceMonitoring;

export const getImpactById = (id) => {
  const data = initializeData();
  const allImpacts = [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts];
  return allImpacts.find(impact => impact.id === id);
};

export const getImpactsByCategory = (category) => {
  const data = initializeData();
  const allImpacts = [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts];
  return allImpacts.filter(impact => impact.category === category);
};

export const getImpactsByMaterialityThreshold = (threshold) => {
  const data = initializeData();
  const allImpacts = [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts];
  return allImpacts.filter(impact => impact.materialityScore >= threshold);
};

export const getHighRiskImpacts = () => {
  const data = initializeData();
  const allImpacts = [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts];
  return allImpacts
    .filter(impact => impact.severity === 'High' || impact.materialityScore >= 8.0)
    .sort((a, b) => b.materialityScore - a.materialityScore);
};

export const calculateImpactTrends = () => {
  return {
    environmentalTrend: '+3.8%',
    socialTrend: '+5.2%',
    governanceTrend: '+2.1%',
    overallTrend: '+3.7%',
    materialityTrend: '+2.4%'
  };
};

export const getMitigationInvestmentSummary = () => {
  const data = initializeData();
  const allImpacts = [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts];
  
  const totalInvestment = allImpacts.reduce((sum, impact) => 
    sum + impact.mitigationMeasures.reduce((mSum, measure) => mSum + measure.investment, 0), 0
  );
  
  const averageEffectiveness = allImpacts.reduce((sum, impact) => 
    sum + impact.mitigationMeasures.reduce((mSum, measure) => mSum + measure.effectiveness, 0) / impact.mitigationMeasures.length, 0
  ) / allImpacts.length;
  
  return {
    totalInvestment,
    averageEffectiveness,
    measureCount: allImpacts.reduce((sum, impact) => sum + impact.mitigationMeasures.length, 0)
  };
};

export const getStakeholderImpactSummary = () => {
  const data = initializeData();
  const allImpacts = [...data.environmentalImpacts, ...data.socialImpacts, ...data.governanceImpacts];
  
  const stakeholders = {};
  allImpacts.forEach(impact => {
    Object.keys(impact.stakeholderImpact).forEach(stakeholder => {
      if (!stakeholders[stakeholder]) {
        stakeholders[stakeholder] = { impacts: 0, severityHigh: 0, severityMedium: 0, severityLow: 0 };
      }
      stakeholders[stakeholder].impacts++;
      const severity = impact.stakeholderImpact[stakeholder].severity;
      if (severity === 'High') stakeholders[stakeholder].severityHigh++;
      else if (severity === 'Medium') stakeholders[stakeholder].severityMedium++;
      else stakeholders[stakeholder].severityLow++;
    });
  });
  
  return stakeholders;
};

export const updateImpactAssessment = (impactId, updateData) => {
  const data = initializeData();
  const impact = getImpactById(impactId);
  
  if (impact) {
    Object.assign(impact, updateData);
    impact.lastAssessment = new Date().toISOString();
    persistData();
  }
};

export const addMitigationMeasure = (impactId, measureData) => {
  const data = initializeData();
  const impact = getImpactById(impactId);
  
  if (impact) {
    impact.mitigationMeasures.push(measureData);
    persistData();
  }
};

export const updatePerformanceMetric = (metricName, newValue) => {
  const data = initializeData();
  const metric = data.performanceMonitoring.find(m => m.name === metricName);
  
  if (metric) {
    metric.current = newValue;
    metric.trend = newValue > metric.current ? `+${((newValue - metric.current) / metric.current * 100).toFixed(1)}%` : 
                   `${((newValue - metric.current) / metric.current * 100).toFixed(1)}%`;
    persistData();
  }
};

export const addImpactAssessment = (assessmentData, category) => {
  const data = initializeData();
  const newAssessment = {
    id: `${category.slice(0, 3).toLowerCase()}-${Date.now()}`,
    ...assessmentData,
    lastAssessment: new Date().toISOString(),
    nextReview: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString() // 90 days from now
  };
  
  if (category === 'environmental') {
    data.environmentalImpacts.push(newAssessment);
  } else if (category === 'social') {
    data.socialImpacts.push(newAssessment);
  } else if (category === 'governance') {
    data.governanceImpacts.push(newAssessment);
  }
  
  persistData();
  return newAssessment;
};

export const generateImpactAssessmentReport = (impactId = null, category = null) => {
  const data = initializeData();
  
  let reportData = {
    overview: getESGImpactAssessmentOverview(),
    integratedMetrics: data.integratedMetrics,
    riskMatrix: data.riskMatrix,
    performanceMonitoring: data.performanceMonitoring,
    generatedAt: new Date().toISOString()
  };

  if (impactId) {
    reportData.impact = getImpactById(impactId);
  }
  
  if (category) {
    reportData.categoryImpacts = getImpactsByCategory(category);
  }

  if (!impactId && !category) {
    reportData.allImpacts = {
      environmental: data.environmentalImpacts,
      social: data.socialImpacts,
      governance: data.governanceImpacts
    };
  }

  return reportData;
};

export const exportImpactAssessmentData = (format = 'json', filterCriteria = {}) => {
  const data = initializeData();
  
  let filteredData = { ...data };
  
  if (filterCriteria.category) {
    const category = filterCriteria.category.toLowerCase();
    if (category === 'environmental') {
      filteredData = { environmentalImpacts: data.environmentalImpacts, integratedMetrics: data.integratedMetrics };
    } else if (category === 'social') {
      filteredData = { socialImpacts: data.socialImpacts, integratedMetrics: data.integratedMetrics };
    } else if (category === 'governance') {
      filteredData = { governanceImpacts: data.governanceImpacts, integratedMetrics: data.integratedMetrics };
    }
  }
  
  if (filterCriteria.materialityThreshold) {
    const threshold = parseFloat(filterCriteria.materialityThreshold);
    filteredData.environmentalImpacts = (filteredData.environmentalImpacts || data.environmentalImpacts)
      .filter(impact => impact.materialityScore >= threshold);
    filteredData.socialImpacts = (filteredData.socialImpacts || data.socialImpacts)
      .filter(impact => impact.materialityScore >= threshold);
    filteredData.governanceImpacts = (filteredData.governanceImpacts || data.governanceImpacts)
      .filter(impact => impact.materialityScore >= threshold);
  }

  return {
    ...filteredData,
    exportedAt: new Date().toISOString(),
    filterCriteria
  };
};

export const refreshImpactAssessmentData = async () => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // In a real application, this would fetch fresh data from the server
  const data = initializeData();
  
  // Update last refresh timestamp
  data.lastRefresh = new Date().toISOString();
  
  // Simulate some data updates
  data.integratedMetrics.overallESGScore = Math.min(100, data.integratedMetrics.overallESGScore + Math.random() * 2 - 1);
  data.integratedMetrics.impactCategories.environmental.score = Math.min(100, data.integratedMetrics.impactCategories.environmental.score + Math.random() * 2 - 1);
  data.integratedMetrics.impactCategories.social.score = Math.min(100, data.integratedMetrics.impactCategories.social.score + Math.random() * 2 - 1);
  data.integratedMetrics.impactCategories.governance.score = Math.min(100, data.integratedMetrics.impactCategories.governance.score + Math.random() * 2 - 1);
  
  persistData();
  return data;
};